export type EmbeddedContentSize = { height: number; width: number };

export function measureEmbeddedContentSize(): EmbeddedContentSize {
  const root = document.getElementById('root');
  return {
    // Measure the content, not the document: the document is never shorter than the
    // frame, so reporting it makes the host frame grow with every report.
    height: Math.ceil(root
      ? Math.max(root.scrollHeight, root.getBoundingClientRect().height)
      : document.body?.scrollHeight ?? document.documentElement.scrollHeight),
    width: Math.ceil(Math.max(document.documentElement.scrollWidth, document.body?.scrollWidth ?? 0, root?.scrollWidth ?? 0)),
  };
}

/** Observe content boxes and DOM changes; documentElement alone only reflects the iframe viewport in some browsers. */
export function observeEmbeddedContentSize(onSize: (size: EmbeddedContentSize) => void): () => void {
  let timer = 0;
  let lastHeight = -1;
  let lastWidth = -1;
  const measure = () => {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => {
      const { height, width } = measureEmbeddedContentSize();
      if (height === lastHeight && width === lastWidth) return;
      lastHeight = height;
      lastWidth = width;
      onSize({ height, width });
    }, 50);
  };
  const resizeObserver = new ResizeObserver(measure);
  [document.documentElement, document.body, document.getElementById('root')]
    .filter((element): element is HTMLElement => element instanceof HTMLElement)
    .forEach((element) => resizeObserver.observe(element));
  const mutationObserver = new MutationObserver(measure);
  mutationObserver.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true });
  window.addEventListener('load', measure);
  window.addEventListener('resize', measure);
  measure();
  return () => {
    resizeObserver.disconnect();
    mutationObserver.disconnect();
    window.removeEventListener('load', measure);
    window.removeEventListener('resize', measure);
    window.clearTimeout(timer);
  };
}

/** True when the app runs inside another page's frame. */
export function isInFrame(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    // A cross-origin parent blocks access to window.top, which means we are framed.
    return true;
  }
}

/**
 * Full-page minimum height, only when the app is the whole page. Inside a frame the
 * content decides the height: a viewport-based height would feed the frame's own
 * height back into the next measurement, so the host frame could only ever grow.
 */
export function fullPageMinHeight(framed: boolean = isInFrame()): string | undefined {
  return framed ? undefined : '100vh';
}

/** Minimum height for loading and status placeholders shown inside a frame. */
export const FRAMED_PLACEHOLDER_MIN_HEIGHT = 320;
